import React, { useCallback, useRef } from 'react';

import { Adult, Bed } from '@travel/icons/service';
import { useDeviceType, MOBILE_SCREEN } from '@travel/traveler-core/hooks';
import useDialogHandler from '@travel/traveler-core/hooks/useDialogHandler';
import { FlatButton, Popup } from '@travel/ui';
import { cx } from '@travel/utils';

import HiddenInput from 'favoriteList/components/SearchForm/components/HiddenInput';
import IconTextButton from 'favoriteList/components/SearchForm/components/IconTextButton';
import labels from 'favoriteList/components/SearchForm/constants/labels';
import type useAmountInputNumberHandlers from 'favoriteList/components/SearchForm/hooks/useAmountInputNumberHandlers';
import { URL_KEYS } from 'favoriteList/components/SearchForm/utils/search';
import AmountInputContent from './AmountInputContent';

import styles from './amountInput.module.scss';

export enum CapacityType {
  ROOMS = 'rooms',
  ADULTS = 'adults',
  CHILDREN_UPPER_GRADE = 'childrenUpperGrade',
  CHILDREN_LOWER_GRADE = 'childrenLowerGrade',
  INFANT_BED_MEAL = 'infantBedMeal',
  INFANT_BED = 'infantBed',
  INFANT_MEAL = 'infantMeal',
  INFANT_NONE = 'infantNone',
}

type Props = {
  className?: string;
  /** String of custom popup classname */
  popupWrapperClassName?: string;
  /** Initial value of room number */
  selectedCapacity: ReturnType<typeof useAmountInputNumberHandlers>;
  /** String to edit default IconTextButtons */
  buttonClassName?: string;
  /** Flag for if searching domestic or overseas hotels */
  isDomestic: boolean;
};

function AmountInput(props: Props) {
  const { isDomestic = true, selectedCapacity } = props;
  const { isOpen, onOpen, onClose } = useDialogHandler(false);
  const {
    prevCounters,
    resetPrevValue,
    adults,
    childrenUpperGrade,
    childrenLowerGrade,
    infantsBedMeal,
    infantsBed,
    infantsMeal,
    infantsNone,
    rooms,
  } = selectedCapacity;

  const onCloseCallback = useCallback(() => {
    rooms.set(prevCounters.current.prevRoomCounter);
    adults.set(prevCounters.current.prevAdultCounter);
    childrenUpperGrade.set(prevCounters.current.prevChildrenUpperGradeCounter);
    childrenLowerGrade.set(prevCounters.current.prevChildrenLowerGradeCounter);
    infantsBedMeal.set(prevCounters.current.prevInfantBedMealCounter);
    infantsBed.set(prevCounters.current.prevInfantBedCounter);
    infantsMeal.set(prevCounters.current.prevInfantMealCounter);
    infantsNone.set(prevCounters.current.prevInfantNoneCounter);
    onClose();
  }, [
    adults,
    childrenUpperGrade,
    childrenLowerGrade,
    infantsBedMeal,
    infantsBed,
    infantsMeal,
    infantsNone,
    onClose,
    prevCounters,
    rooms,
  ]);

  const onClickPrimaryCallback = useCallback(() => {
    resetPrevValue();
    onClose();
  }, [onClose, resetPrevValue]);

  const isSP = useDeviceType() === MOBILE_SCREEN;
  const anchorEl = useRef<HTMLDivElement>(null);
  const numberOfChildren =
    prevCounters.current.prevChildrenLowerGradeCounter +
    prevCounters.current.prevChildrenUpperGradeCounter +
    prevCounters.current.prevInfantBedCounter +
    prevCounters.current.prevInfantBedMealCounter +
    prevCounters.current.prevInfantMealCounter +
    prevCounters.current.prevInfantNoneCounter;

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        onOpen();
      }
    },
    [onOpen],
  );

  return (
    <>
      {!isSP ? (
        <div className={cx(styles.row, props.className)} ref={anchorEl}>
          <div
            role="button"
            tabIndex={-1}
            onKeyDown={handleKeyDown}
            onClick={onOpen}
            className={cx(styles.rowButton)}
          >
            <IconTextButton
              className={cx(
                styles.room,
                styles.rowElement,
                props.buttonClassName,
                isOpen && styles.popupOpen,
              )}
              text={
                <span className={styles.smallishText}>
                  {prevCounters.current.prevRoomCounter} {labels.rooms}
                </span>
              }
              icon={<Bed size={20} />}
              onClick={onOpen}
              data-testid="amountInput-roomCount-button"
            />
            <div className={styles.silverBorderDivider} />
            <IconTextButton
              className={cx(
                styles.person,
                styles.rowElement,
                props.buttonClassName,
                isOpen && styles.popupOpen,
              )}
              icon={<Adult size={20} />}
              onClick={onOpen}
              data-testid="amountInput-personCount-button"
              text={
                <>
                  <span className={styles.personText}>
                    {labels.adult}{' '}
                    <span className={styles.smallishText}>
                      {prevCounters.current.prevAdultCounter}
                    </span>
                    <span className={styles.personTextCounter}>{labels.peopleCounterPC}</span>
                  </span>
                  &nbsp;
                  <span className={styles.personText}>
                    {labels.children}{' '}
                    <span className={styles.smallishText}>{numberOfChildren}</span>
                    <span className={styles.personTextCounter}>{labels.peopleCounterPC}</span>
                  </span>
                </>
              }
            />
          </div>
          <Popup
            isOpen={isOpen}
            onClose={onCloseCallback}
            className={styles.popupWrapper}
            anchorEl={anchorEl.current}
          >
            <div className={styles.popupContainer}>
              <div className={styles.popupContent}>
                <AmountInputContent
                  adults={adults}
                  childrenUpperGrade={childrenUpperGrade}
                  childrenLowerGrade={childrenLowerGrade}
                  infantsBedMeal={infantsBedMeal}
                  infantsBed={infantsBed}
                  infantsMeal={infantsMeal}
                  infantsNone={infantsNone}
                  rooms={rooms}
                  isDomestic={isDomestic}
                />
              </div>
            </div>
            <div className={styles.actionButtonWrapper}>
              <FlatButton
                classType="list"
                className={styles.actionButton}
                type="button"
                onClick={onClickPrimaryCallback}
              >
                {labels.decide}
              </FlatButton>
            </div>
          </Popup>
        </div>
      ) : (
        <AmountInputContent
          adults={adults}
          childrenUpperGrade={childrenUpperGrade}
          childrenLowerGrade={childrenLowerGrade}
          infantsBedMeal={infantsBedMeal}
          infantsBed={infantsBed}
          infantsMeal={infantsMeal}
          infantsNone={infantsNone}
          rooms={rooms}
          isDomestic={isDomestic}
        />
      )}
      <HiddenInput value={rooms.counter} id={URL_KEYS.NO_OF_UNITS} />
      <HiddenInput value={adults.counter} id={URL_KEYS.ADULTS} />
      <HiddenInput value={childrenUpperGrade.counter} id={URL_KEYS.CHILDREN_UPPER_GRADE} />
      {isDomestic && (
        <>
          <HiddenInput value={childrenLowerGrade.counter} id={URL_KEYS.CHILDREN_LOWER_GRADE} />
          <HiddenInput value={infantsBedMeal.counter} id={URL_KEYS.INFANT_BED_MEALS} />
          <HiddenInput value={infantsBed.counter} id={URL_KEYS.INFANT_BED} />
          <HiddenInput value={infantsMeal.counter} id={URL_KEYS.INFANT_MEAL} />
        </>
      )}
      <HiddenInput value={infantsNone.counter} id={URL_KEYS.INFANT_NONE} />
    </>
  );
}

export default AmountInput;
